import { motion } from 'framer-motion';

interface TestResultProps {
  answers: Record<number, string | string[]>;
  onRestart: () => void;
}

const TestResult = ({ answers, onRestart }: TestResultProps) => {
  // 智能评估逻辑
  const calculateResult = () => {
    let score = 0;
    const riskPoints: string[] = [];
    const advantages: string[] = [];
    
    // 主体适配评估 (Question 1)
    if (answers[1] === '已有市场内个体工商户') {
      score += 25;
      advantages.push('已具备1039合法申报主体');
    } else if (answers[1] === '有公司但无个体户') {
      score += 15;
      riskPoints.push('需补办个体工商户营业执照');
    } else if (answers[1] === '暂无任何主体') {
      score += 10;
      riskPoints.push('需注册市场内个体工商户');
    }

    // 禁限商品判断 (Question 3)
    if (answers[3] === '确定不涉及') {
      score += 20;
      advantages.push('商品符合1039出口范围');
    } else if (answers[3] === '不确定') {
      score += 10;
      riskPoints.push('需进行商品合规性复核');
    } else if (answers[3] === '可能涉及') {
      score -= 10;
      riskPoints.push('可能涉及禁限类商品，存在合规风险');
    }

    // 金额判断 (Question 4)
    if (answers[4] === '≤5万美元' || answers[4] === '5-10万美元' || answers[4] === '10-15万美元') {
      score += 25;
      advantages.push('单票金额符合1039限额要求');
    } else if (answers[4] === '>15万美元') {
      score -= 30;
      riskPoints.push('单票金额超出1039限制（15万美元）');
    }

    // 物流评估 (Question 6)
    if (answers[6] === '有且支持1039') {
      score += 15;
      advantages.push('物流渠道已具备1039操作能力');
    } else if (answers[6] === '有但不确定支持' || answers[6] === '有但不支持') {
      score += 5;
      riskPoints.push('需确认或更换支持1039的物流商');
    } else if (answers[6] === '暂无合作物流商') {
      score += 5;
      riskPoints.push('需对接支持1039的物流服务商');
    }

    // 监管仓接受度 (Question 7)
    if (answers[7] === '完全接受' || answers[7] === '可以接受') {
      score += 15;
      advantages.push('接受监管仓操作模式');
    } else if (answers[7] === '不太愿意') {
      score += 5;
      riskPoints.push('需了解监管仓操作的必要性和优势');
    } else if (answers[7] === '不能接受') {
      score -= 20;
      riskPoints.push('1039必须通过监管仓操作，不接受将无法使用');
    }

    return { score: Math.max(0, Math.min(100, score)), riskPoints, advantages };
  };

  const result = calculateResult();
  const { score, riskPoints, advantages } = result;

  // 判断结果类型
  const getResultType = () => {
    if (score >= 75) return 'suitable';      // 适合
    if (score >= 45) return 'conditional';   // 条件适合  
    return 'unsuitable';                     // 暂不适合
  };

  const resultType = getResultType();

  const resultConfig = {
    suitable: {
      title: '✅ 适合使用1039',
      subtitle: '您的业务非常适合1039市场采购贸易模式',
      color: 'text-green-600',
      bgColor: 'from-green-500 to-green-600',
      icon: 'ri-checkbox-circle-fill',
      iconBg: 'bg-green-100',
      iconColor: 'text-green-500',
      circleColor: '#00C853',
      description: '根据评估结果，您的业务结构、商品类型、交易金额等各方面都非常符合1039的政策要求。建议尽快入驻我们的数字化系统，开启合规高效的出口之旅。'
    },
    conditional: {
      title: '⚠️ 条件适合使用1039',
      subtitle: '您的业务基本符合条件，需完善部分环节',
      color: 'text-amber-600',
      bgColor: 'from-amber-500 to-amber-600',
      icon: 'ri-error-warning-fill',
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-500',
      circleColor: '#FFB800',
      description: '您的业务基本符合1039使用条件，但在某些环节还需要完善。建议咨询我们的专业顾问，制定针对性的解决方案，优化业务流程以更好地适配1039政策。'
    },
    unsuitable: {
      title: '❌ 暂不适合使用1039',
      subtitle: '建议考虑其他贸易方式或调整业务结构',
      color: 'text-red-600',
      bgColor: 'from-red-500 to-red-600',
      icon: 'ri-close-circle-fill',
      iconBg: 'bg-red-100',
      iconColor: 'text-red-500',
      circleColor: '#F44336',
      description: '根据当前评估，您的业务可能更适合其他贸易方式。我们建议联系专业顾问进行详细咨询，为您量身定制最优的出口贸易解决方案。'
    }
  };

  const config = resultConfig[resultType];

  return (
    <section className="py-24 min-h-screen bg-gradient-to-br from-neutral-50 to-white">
      <div className="max-w-7xl mx-auto px-8">
        {/* 结果标题区 */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          {/* 结果图标 */}
          <div className={`w-32 h-32 mx-auto mb-8 rounded-full flex items-center justify-center ${config.iconBg}`}>
            <i className={`${config.icon} text-7xl ${config.iconColor}`}></i>
          </div>

          {/* 结果标题 */}
          <h1 className="text-5xl font-bold text-primary mb-4">
            {config.title}
          </h1>
          <p className="text-xl text-neutral-600 mb-8 max-w-3xl mx-auto">
            {config.subtitle}
          </p>

          {/* 适配度分数圆环 */}
          <div className="relative w-56 h-56 mx-auto mb-8">
            <svg className="transform -rotate-90 w-56 h-56">
              <circle
                cx="112"
                cy="112"
                r="100"
                stroke="#E0E0E0"
                strokeWidth="16"
                fill="none"
              />
              <motion.circle
                cx="112"
                cy="112"
                r="100"
                stroke={config.circleColor}
                strokeWidth="16"
                fill="none"
                strokeLinecap="round"
                initial={{ strokeDasharray: '0 628' }}
                animate={{ strokeDasharray: `${(score / 100) * 628} 628` }}
                transition={{ duration: 2, ease: 'easeOut' }}
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className="text-6xl font-bold text-primary">{score}</div>
                <div className="text-lg text-neutral-500 font-medium">适配度评分</div>
              </div>
            </div>
          </div>

          <p className="text-lg text-neutral-600 max-w-4xl mx-auto leading-relaxed">
            {config.description}
          </p>
        </motion.div>

        {/* 详细分析区 */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="grid grid-cols-2 gap-12 mb-16"
        >
          {/* 可用政策点 */}
          <div className="bg-white rounded-3xl p-10 shadow-xl">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <i className="ri-shield-check-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-primary">政策优势</h3>
            </div>
            <div className="space-y-4">
              {advantages.length > 0 ? advantages.map((advantage, index) => (
                <div key={index} className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-xl text-green-500 mt-1 flex-shrink-0"></i>
                  <span className="text-neutral-700 font-medium">{advantage}</span>
                </div>
              )) : (
                <div className="text-neutral-500 text-center py-8">
                  <i className="ri-information-line text-4xl mb-4"></i>
                  <p>根据当前评估，建议优化业务结构以获得更多政策优势</p>
                </div>
              )}
            </div>
          </div>

          {/* 风险提示 */}
          <div className="bg-white rounded-3xl p-10 shadow-xl">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <i className="ri-error-warning-line text-2xl text-amber-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-primary">需要关注</h3>
            </div>
            <div className="space-y-4">
              {riskPoints.length > 0 ? riskPoints.map((risk, index) => (
                <div key={index} className="flex items-start gap-3">
                  <i className="ri-error-warning-fill text-xl text-amber-500 mt-1 flex-shrink-0"></i>
                  <span className="text-neutral-700 font-medium">{risk}</span>
                </div>
              )) : (
                <div className="text-green-600 text-center py-8">
                  <i className="ri-shield-check-line text-4xl mb-4"></i>
                  <p className="font-medium">暂无明显风险点，业务结构良好</p>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* 推荐行动路径 */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mb-16"
        >
          <h2 className="text-3xl font-bold text-primary text-center mb-12">推荐行动路径</h2>
          
          {resultType === 'suitable' && (
            <div className="grid grid-cols-3 gap-8">
              <div className="bg-gradient-to-br from-primary to-primary-dark rounded-3xl p-10 text-white text-center">
                <i className="ri-rocket-line text-5xl mb-6"></i>
                <h3 className="text-2xl font-bold mb-4">立即入驻系统</h3>
                <p className="text-white/80 mb-8 leading-relaxed">
                  您的条件已完全满足，可直接进入1039数字化系统开始操作
                </p>
                <button className="w-full py-4 bg-white text-primary font-bold rounded-xl hover:bg-accent transition-all duration-300 cursor-pointer whitespace-nowrap">
                  进入数字化系统
                </button>
              </div>

              <div className="bg-gradient-to-br from-accent to-accent-dark rounded-3xl p-10 text-primary text-center">
                <i className="ri-customer-service-2-line text-5xl mb-6"></i>
                <h3 className="text-2xl font-bold mb-4">专员对接服务</h3>
                <p className="text-primary/80 mb-8 leading-relaxed">
                  安排专业合规专员为您提供一对一入驻指导服务
                </p>
                <button className="w-full py-4 bg-primary text-white font-bold rounded-xl hover:bg-primary-dark transition-all duration-300 cursor-pointer whitespace-nowrap">
                  预约专员对接
                </button>
              </div>

              <div className="bg-white rounded-3xl p-10 shadow-xl text-center border-2 border-neutral-200">
                <i className="ri-download-cloud-line text-5xl text-primary mb-6"></i>
                <h3 className="text-2xl font-bold text-primary mb-4">下载评估报告</h3>
                <p className="text-neutral-600 mb-8 leading-relaxed">
                  获取完整的适配度分析报告，保存备用参考
                </p>
                <button className="w-full py-4 bg-neutral-100 text-primary font-bold rounded-xl hover:bg-neutral-200 transition-all duration-300 cursor-pointer whitespace-nowrap">
                  下载PDF报告
                </button>
              </div>
            </div>
          )}

          {resultType === 'conditional' && (
            <div className="grid grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-accent to-accent-dark rounded-3xl p-10 text-primary text-center">
                <i className="ri-user-settings-line text-5xl mb-6"></i>
                <h3 className="text-2xl font-bold mb-4">咨询专业顾问</h3>
                <p className="text-primary/80 mb-8 leading-relaxed">
                  专业顾问将为您制定针对性解决方案，优化业务流程
                </p>
                <button className="w-full py-4 bg-primary text-white font-bold rounded-xl hover:bg-primary-dark transition-all duration-300 cursor-pointer whitespace-nowrap">
                  预约专业咨询
                </button>
              </div>

              <div className="bg-white rounded-3xl p-10 shadow-xl text-center border-2 border-accent">
                <i className="ri-file-list-3-line text-5xl text-accent mb-6"></i>
                <h3 className="text-2xl font-bold text-primary mb-4">查看优化建议</h3>
                <p className="text-neutral-600 mb-8 leading-relaxed">
                  获取详细的业务优化建议，逐步完善适配条件
                </p>
                <button className="w-full py-4 bg-accent text-primary font-bold rounded-xl hover:bg-accent-dark transition-all duration-300 cursor-pointer whitespace-nowrap">
                  查看详细建议
                </button>
              </div>
            </div>
          )}

          {resultType === 'unsuitable' && (
            <div className="grid grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-neutral-600 to-neutral-700 rounded-3xl p-10 text-white text-center">
                <i className="ri-questionnaire-line text-5xl mb-6"></i>
                <h3 className="text-2xl font-bold mb-4">咨询其他方案</h3>
                <p className="text-white/80 mb-8 leading-relaxed">
                  了解一般贸易、跨境电商等其他出口贸易方式
                </p>
                <button className="w-full py-4 bg-white text-neutral-700 font-bold rounded-xl hover:bg-neutral-100 transition-all duration-300 cursor-pointer whitespace-nowrap">
                  咨询其他方案
                </button>
              </div>

              <div className="bg-white rounded-3xl p-10 shadow-xl text-center border-2 border-neutral-300">
                <i className="ri-phone-line text-5xl text-primary mb-6"></i>
                <h3 className="text-2xl font-bold text-primary mb-4">留下联系方式</h3>
                <p className="text-neutral-600 mb-8 leading-relaxed">
                  获取定制化出口贸易解决方案建议
                </p>
                <button className="w-full py-4 bg-primary text-white font-bold rounded-xl hover:bg-primary-dark transition-all duration-300 cursor-pointer whitespace-nowrap">
                  获取专业建议
                </button>
              </div>
            </div>
          )}
        </motion.div>

        {/* 重新测试 */}
        <div className="text-center">
          <button
            onClick={onRestart}
            className="text-neutral-500 hover:text-primary transition-colors underline cursor-pointer whitespace-nowrap text-lg"
          >
            重新进行可行性评估
          </button>
        </div>

        {/* 底部合规声明 */}
        <div className="mt-16 bg-neutral-100 rounded-2xl p-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <i className="ri-shield-keyhole-line text-2xl text-primary"></i>
            <h4 className="text-lg font-bold text-primary">合规声明</h4>
          </div>
          <p className="text-neutral-600 max-w-4xl mx-auto leading-relaxed">
            本评估结果基于当前填报信息的初步判断，仅供参考。最终业务合规性以海关总署、商务部等相关监管部门的最新政策要求为准。
            建议在实际操作前咨询专业顾问，确保业务完全合规。
          </p>
        </div>
      </div>
    </section>
  );
};

export default TestResult;
